// Imports
import React, { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import AppContext from '../../context';


// Styled component
const Container = styled.ins`
	margin-top: 1.875rem;
`;


// Extend interface
declare global {
	interface Window {
		adsbygoogle?: unknown[];
	}
}


// Load the Google Ads script once, and resolve when it's ready
let adsScriptPromise: Promise<void> | null = null;

const loadAdsScript = () => {
	if (!adsScriptPromise) {
		adsScriptPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.async = true;
			script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
			
			script.addEventListener('load', () => resolve());
			script.addEventListener('error', () => {
				adsScriptPromise = null;
				script.remove();
				reject(new Error('Google Ads script failed to load'));
			});
			
			const firstScript = document.getElementsByTagName('script')[0];
			firstScript.parentNode?.insertBefore(script, firstScript);
		});
	}
	
	return adsScriptPromise;
};


// Function component
const GoogleAds: React.FC = () => {
	// Use ref
	const adRef = useRef<HTMLModElement>(null);
	const hasPushedRef = useRef(false);
	
	
	// Use React Router functionality
	const location = useLocation();
	
	
	// Use context
	const appContainerRef = useContext(AppContext)?.appContainerRef;
	
	
	// Trigger ad if necessary
	useEffect(() => {
		// Initialize
		const ad = adRef.current;
		let cancelled = false;
		let resizeObserver: ResizeObserver | null = null;
		
		
		// Function that pushes this slot's ad if it's ready and returns whether we're done waiting to push it
		const pushAd = () => {
			// Stop waiting if there's no slot, or if an ad is already filled or requested for this slot
			if (!ad || ad.dataset.adsbygoogleStatus || hasPushedRef.current) {
				return true;
			}
			
			
			// Keep waiting if the slot has no width yet
			if (ad.offsetWidth === 0) {
				return false;
			}
			
			
			// Request an ad for this slot and push the ad
			hasPushedRef.current = true;
			
			(window.adsbygoogle = window.adsbygoogle || []).push({});
			
			
			// Return that we're done waiting, since the ad has been requested
			return true;
		};
		
		
		// Trigger ad once the script is ready
		loadAdsScript().then(
			() => {
				// Stop if the effect was cleaned up while the script loaded, or if we're done waiting to push the ad
				if (cancelled || !ad || pushAd()) {
					return;
				}
				
				
				// Try again whenever the slot resizes, and stop watching once we're done waiting
				resizeObserver = new ResizeObserver(() => {
					if (pushAd()) {
						resizeObserver?.disconnect();
					}
				});
				
				
				// Watch the slot for when it gets a width
				resizeObserver.observe(ad);
			},
			() => {
				// Do nothing if the ad script didn't load
			}
		);
		
		
		// Capture current ref
		const node = appContainerRef?.current;
		
		
		// Watch for and undo injected style that breaks page layout
		const observer = new MutationObserver(() => {
			if (node?.style.minHeight) {
				node.style.minHeight = '';
			}
		});
		
		if (node) {
			observer.observe(node, { attributeFilter: ['style'] });
		}
		
		
		// Clean-up
		return () => {
			// Stop watching for injected style
			observer.disconnect();
			
			
			// Undo injected style that breaks page layout
			if (node) {
				node.style.minHeight = '';
			}
			
			
			// Stop waiting to trigger this slot's ad
			cancelled = true;
			resizeObserver?.disconnect();
		};
	}, [location.pathname, appContainerRef]);
	
	
	// Return JSX
	return (
		<Container
			ref={adRef}
			className='adsbygoogle'
			style={{ display: 'block', overflow: 'hidden' }}
			data-ad-client='ca-pub-5774021149745769'
			data-ad-slot='1591685952'
			data-ad-format='auto'
		/>
	);
};

export default GoogleAds;
